import {
  Component,
  Output,
  Input,
  EventEmitter,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { ManageNonUsfProductsService } from '@inventory/pages/manage-non-usf-products/services/manage-non-usf-products.service';
import { TranslateService } from '@ngx-translate/core';
import { PlatformEnum, PlatformService } from '@panamax/app-state';
import { BaseDualPaneModalContainerComponent } from '@shared/components/modal-with-nav-inline/dual-pane-container/base-dual-pane-modal-container/base-dual-pane-modal-container.component';
import {
  dualPaneDefaultVerifySave,
  getSearchableObservable$,
  updateRightPaneWithValue,
} from '@shared/components/modal-with-nav-inline/dual-pane-container/Helpers/dual-pane-helper-functions';
import { DualPaneView } from '@shared/constants/dual-pane.enum';
import { AddToListStrings } from '@shared/constants/lists-constants';
import { PATHS } from '@shared/constants/paths';
import {
  BasicListItem,
  DualPaneFlagsConfig,
  DualPaneFunctionsConfig,
  DualPaneLabelsConfig,
  DualPaneViewModel,
  LeftPaneInfo,
  RightPaneInfo,
} from '@shared/models/dual-pane-model';
import { Product } from '@shared/models/product.model';
import { BehaviorSubject, map, Observable, of, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-add-non-usf-product-container',
  templateUrl: './add-non-usf-product-container.component.html',
  styleUrls: ['./add-non-usf-product-container.component.scss'],
})
export class AddNonUsfProductContainerComponent extends BaseDualPaneModalContainerComponent {
  @Output() dismissEvent: EventEmitter<void> = new EventEmitter();
  @Output() submitEvent: EventEmitter<Product[]> = new EventEmitter();

  @Input() platform: PlatformEnum;

  constructor(
    public platformService: PlatformService,
    private manageNonUsfProductsService: ManageNonUsfProductsService,
    public translateService: TranslateService,
  ) {
    super(platformService);
  }

  async openModal() {
    await this.createNonUsfProductVm();
    this.isModalOpen$.next(true);
  }

  closeModal() {
    this.isModalOpen$.next(false);
    this.dismissEvent.emit();
  }

  getNonUsfData$ = () => {
    return this.manageNonUsfProductsService.getNonUsfRadioItemsAsProducts$();
  };

  createNonUsfProductVm = async () => {
    const labelConfig: DualPaneLabelsConfig = {
      dataCy: 'add-to-list',
      title: 'i18n.lists.addToListModal.addNonUsfProducts',
      subTitle: 'i18n.lists.addToListInfo',
      submitText: 'i18n.common.submit',
      backText: 'i18n.common.save',
    };
    const functionConfig: DualPaneFunctionsConfig = {
      intialDesktopTabletView: this.buildNonUsfRightPane,
      handleSubmit: this.submitModalValues,
      verifySubmit: this.verifySubmission,
    };

    const flagConfig: DualPaneFlagsConfig = {};

    const contentItems: BasicListItem[] = [
      {
        label: 'i18n.lists.addToListModal.chooseNonUsfProducts',
        placeHolder: 'i18n.lists.addToListModal.noProductsSelected',
        subLabel: 'i18n.lists.addToListModal.noProductsSelected',
        view: DualPaneView.multiSelectNonUsf,
        selected: true,
        onSelectfunction: this.buildNonUsfRightPane,
      },
    ];

    labelConfig.subTitle = undefined;
    labelConfig.complexSubTitle = [
      {
        text: 'i18n.lists.addToListInfo',
        isBold: false,
      },
      {
        text: 'i18n.lists.addToListModal.visitThe',
        isBold: false,
      },
      {
        text: 'i18n.lists.addToListModal.nonUsfProductsPage',
        path: [PATHS.INVENTORY, PATHS.MANAGE_NON_USF_PRODUCTS],
        isBold: false,
      },
      {
        text: 'i18n.lists.addToListModal.toAddRemove',
        isBold: false,
      },
    ];

    const isMobile = this.platformService.platformType === PlatformEnum.mobile;

    this.dualPaneModalData = {
      dataCyTag: labelConfig.dataCy,
      viewModel: {
        title: labelConfig.title,
        subTitle: labelConfig.subTitle,
        complexSubTitle: labelConfig.complexSubTitle,
        htmlSubTitle: labelConfig.htmlSubTitle,
        navigationPageCount: 0,
        leftView: {
          view: isMobile ? DualPaneView.noView : DualPaneView.basicList,
          content: contentItems,
        },
        rightView: isMobile
          ? this.buildNonUsfRightPane(
              { leftView: { content: contentItems } } as DualPaneViewModel,
              '',
            )
          : functionConfig.intialDesktopTabletView({} as DualPaneViewModel, ''),
        disableSubmit: true,
        disableSave: true,
        submitText: labelConfig.submitText,
        cancelText: labelConfig.cancelText,
        submitIsBackButton: false,
        backText: labelConfig.backText,
        isLoading$: this.isLoading$,
        handleSubmission: functionConfig.handleSubmit,
        verifySubmissionValidity: functionConfig.verifySubmit,
        platform: this.platformService.platformType,
        dismissOnSubmit: flagConfig.dismissOnSubmit ?? false,
        overrideDismiss: functionConfig.overrideDismiss,
        outsideLifecycleOperation: functionConfig.outsideLifecycleOperation,
      },
      platform: this.platformService.platformType,
      modalOpenState$: this.isModalOpen$,
      submit$: this.submit$,
      outsideLifecycle$: this.outsideLifecycle$,
    };
  };

  buildNonUsfRightPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    const valueArray = viewModel?.leftView?.content[0].value ?? [];
    let selectedCount = 0;
    const itemsObservable$: Observable<Product[]> = this.getNonUsfData$().pipe(
      map(items => {
        return items.map(item => {
          return {
            ...item,
            nonUSFData: {
              ...item.nonUSFData,
              selected: valueArray?.find(val => {
                if (val.productNumber === item.productNumber) {
                  selectedCount++;
                }
                return val.productNumber === item.productNumber;
              }),
            },
          };
        });
      }),
    );
    const nonUsfPane = {
      view: DualPaneView.multiSelectNonUsf,
      title: AddToListStrings.chooseNonUsf,
      error: of(false),
      verifySaveButton: dualPaneDefaultVerifySave,
      searchPane: {
        hideSearch: false,
        onSearchFunction: this.nonUsfSearch,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
        noResultsOverride: 'i18n.requestCredit.noProductNumbersFound',
        searchPlaceholder: 'i18n.lists.addToListModal.searchForProduct',
      },
      itemsObservable$,
      handleItemSelection: this.nonUsfSelection,
      overrideSaveOnNavigation: this.nonUsfSaveOnNavigation,
    } as RightPaneInfo;
    nonUsfPane.searchPane.searchableObservable$ =
      getSearchableObservable$(nonUsfPane);
    updateRightPaneWithValue(viewModel, paneSelected, nonUsfPane);
    return nonUsfPane;
  };

  nonUsfSelection = (
    left: LeftPaneInfo,
    platform: PlatformEnum,
    right: RightPaneInfo,
    value: any,
  ): void => {
    let tempArray = [...(right.value ?? [])] as any[];
    if (value?.nonUSFData?.selected) {
      tempArray.push(value);
    } else {
      tempArray = tempArray.filter(
        item => item.productNumber !== value.productNumber,
      );
    }
    right.value = tempArray;

    let subLabel = undefined;
    subLabel =
      this.translateService.instant(
        'i18n.lists.addToListModal.productsSelected',
      ) +
      ': ' +
      tempArray.length;

    left.content = left.content.map(item => {
      if (item.selected) {
        return {
          ...item,
          subLabel,
          value: tempArray,
        };
      } else {
        return item;
      }
    });
  };

  nonUsfSaveOnNavigation = (
    vm: DualPaneViewModel,
    item: BasicListItem,
    value: any,
  ) => {
    let subLabel = undefined;
    subLabel =
      this.translateService.instant(
        'i18n.lists.addToListModal.productsSelected',
      ) +
      ': ' +
      value.length;
    item.value = value;
    return item;
  };

  nonUsfSearch = (
    rightPane: RightPaneInfo,
    searchKey: string,
    items: any[],
  ) => {
    const searchedItems = items.filter(item => {
      return (
        item?.summary?.productDescTxtl
          ?.toLowerCase()
          ?.includes((searchKey || '').toLowerCase()) ||
        item?.nonUSFData?.vendorName
          ?.toLowerCase()
          ?.includes((searchKey || '').toLowerCase()) ||
        item?.nonUSFData?.manufacturerProductNumber
          ?.toLowerCase()
          ?.includes((searchKey || '').toLowerCase())
      );
    });
    return searchedItems;
  };

  verifySubmission = (vm: DualPaneViewModel) => {
    let valid = true;
    vm.leftView.content.forEach(item => {
      if (!item.value || item.value.length === 0) {
        valid = false;
      }
    });
    return valid;
  };

  submitModalValues = async (vm: DualPaneViewModel) => {
    const submitMap = new Map<string, any | any[]>();

    submitMap.set('nonUsfProduct', vm.leftView.content[0]?.value);

    this.submit$.next(submitMap);
    this.closeModalAndSubmit();
  };

  closeModalAndSubmit() {
    this.isModalOpen$.next(false);
    if (this.submit$.value !== undefined) {
      this.submitEvent.emit(this.submit$.value.get('nonUsfProduct'));
    }
  }
}
